import { useState } from 'react';
import Box from '@mui/material/Box';
import Chip from '@mui/material/Chip';
import Collapse from '@mui/material/Collapse';
import IconButton from '@mui/material/IconButton';
import Slider from '@mui/material/Slider';
import Switch from '@mui/material/Switch';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import CloseIcon from '@mui/icons-material/Close';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ReportProblemOutlinedIcon from '@mui/icons-material/ReportProblemOutlined';
import ZoomInIcon from '@mui/icons-material/ZoomIn';
import ZoomInMapIcon from '@mui/icons-material/ZoomInMap';
import ZoomOutIcon from '@mui/icons-material/ZoomOut';
import type { ActiveLayer, ScaleVisibility } from '@okmaps/shared';
import { useAppDispatch } from '../../app/hooks.js';
import {
  layerOpacityChanged,
  layerRemoved,
  layerReordered,
  layerVisibilityToggled,
} from '../../store/slices/layersSlice.js';
import { zoomToLayer } from '../../store/thunks/zoomToLayer.js';
import { legendUrl, useGetLayerHealthQuery } from '../../services/layerApi.js';

interface Props {
  readonly layer: ActiveLayer;
  readonly storeIndex: number;
  readonly isTop: boolean;
  readonly isBottom: boolean;
  readonly total: number;
  /** Where the current view sits in this layer's scale window, if it has one. */
  readonly scaleVisibility: ScaleVisibility | undefined;
}

/**
 * One layer on the map.
 *
 * Reordering is done with buttons rather than drag-and-drop. Drag is the more
 * obvious gesture, but it is unusable by keyboard and awkward on touch, and a
 * layer stack is exactly the kind of control someone rebuilds with a screen
 * reader open. Buttons cost one click each and work for everyone.
 */
export default function LayerListItem({
  layer,
  storeIndex,
  isTop,
  isBottom,
  total,
  scaleVisibility,
}: Props) {
  const dispatch = useAppDispatch();
  const [expanded, setExpanded] = useState(false);

  // Only asked for layers actually on the map, and answered from cache after
  // the first time: the proxy probes upstream to answer it.
  const { data: health } = useGetLayerHealthQuery(layer.name);

  const outOfScale = scaleVisibility === 'zoom-in' || scaleVisibility === 'zoom-out';
  const undrawable = health?.status === 'error';
  // A layer switched on that still shows nothing needs to say why, and the two
  // reasons look identical on a map.
  const explained = layer.visible && (outOfScale || undrawable);

  const move = (direction: 'up' | 'down') => {
    // "Up" in the list means later in the store's bottom-first draw order.
    const to = direction === 'up' ? storeIndex + 1 : storeIndex - 1;
    dispatch(layerReordered({ from: storeIndex, to }));
  };

  return (
    <Box component="li" sx={{ borderBottom: 1, borderColor: 'divider' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, px: 1, py: 0.75 }}>
        <Switch
          size="small"
          checked={layer.visible}
          onChange={() => dispatch(layerVisibilityToggled(layer.name))}
          slotProps={{ input: { 'aria-label': `Show ${layer.title}` } }}
        />

        <Typography
          variant="body2"
          sx={{
            flex: 1,
            minWidth: 0,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            /**
             * Muted with the theme's own secondary text rather than with
             * opacity. Half-opacity primary text lands at 3.2:1 on white, which
             * fails WCAG AA - and the state being signalled here is exactly the
             * one where the user most needs to read which layer it is.
             */
            color: layer.visible && !explained ? 'text.primary' : 'text.secondary',
          }}
          title={layer.title}
        >
          {layer.title}
        </Typography>

        <Tooltip title="Move up">
          <span>
            <IconButton
              size="small"
              disabled={isTop}
              onClick={() => move('up')}
              aria-label={`Move ${layer.title} up. Currently ${storeIndexLabel(storeIndex, total)}`}
            >
              <ArrowUpwardIcon sx={{ fontSize: 15 }} />
            </IconButton>
          </span>
        </Tooltip>

        <Tooltip title="Move down">
          <span>
            <IconButton
              size="small"
              disabled={isBottom}
              onClick={() => move('down')}
              aria-label={`Move ${layer.title} down. Currently ${storeIndexLabel(storeIndex, total)}`}
            >
              <ArrowDownwardIcon sx={{ fontSize: 15 }} />
            </IconButton>
          </span>
        </Tooltip>

        <Tooltip title="Zoom to layer">
          <IconButton
            size="small"
            onClick={() => void dispatch(zoomToLayer(layer.name))}
            aria-label={`Zoom the map to ${layer.title}`}
          >
            <ZoomInMapIcon sx={{ fontSize: 16 }} />
          </IconButton>
        </Tooltip>

        <Tooltip title={expanded ? 'Hide options' : 'Show options'}>
          <IconButton
            size="small"
            onClick={() => setExpanded((v) => !v)}
            aria-expanded={expanded}
            aria-label={`${expanded ? 'Hide' : 'Show'} options for ${layer.title}`}
          >
            <ExpandMoreIcon
              sx={{ fontSize: 17, transform: expanded ? 'rotate(180deg)' : 'none', transition: '0.15s' }}
            />
          </IconButton>
        </Tooltip>

        <Tooltip title="Remove from map">
          <IconButton
            size="small"
            onClick={() => dispatch(layerRemoved(layer.name))}
            aria-label={`Remove ${layer.title} from the map`}
          >
            <CloseIcon sx={{ fontSize: 16 }} />
          </IconButton>
        </Tooltip>
      </Box>

      {explained && (
        /**
         * Indented to the title rather than to the row, so the explanation
         * hangs off the layer it is about instead of floating under the switch.
         */
        <Box sx={{ pl: 6.5, pr: 1, pb: 0.75, display: 'flex' }}>
          {undrawable ? (
            /**
             * Published, but upstream cannot render it - a NOAA host that no
             * longer resolves, a missing granule file, an empty layer group.
             * Stated rather than left as a blank map, the same way the catalog
             * labels a record with no map service instead of hiding it.
             */
            <Tooltip title={health.message ?? ''}>
              <Chip
                size="small"
                variant="outlined"
                icon={<ReportProblemOutlinedIcon />}
                label="Server cannot draw this layer"
                /**
                 * Oklahoma gold carries the warning on the border and the icon,
                 * not on the text. As a label color it measures 1.7:1 on white:
                 * the palette's gold is a fill, and reading it as a foreground
                 * is how a warning ends up being the least legible thing in the
                 * panel.
                 */
                sx={{
                  maxWidth: '100%',
                  color: 'text.primary',
                  borderColor: 'warning.main',
                  '& .MuiChip-icon': { color: 'warning.main' },
                }}
              />
            </Tooltip>
          ) : (
            /**
             * Outside its scale window. The chip is the fix, not a notice about
             * one: clicking it goes to a scale where the layer draws.
             */
            <Chip
              size="small"
              variant="outlined"
              clickable
              onClick={() => void dispatch(zoomToLayer(layer.name))}
              icon={scaleVisibility === 'zoom-in' ? <ZoomInIcon /> : <ZoomOutIcon />}
              label={scaleVisibility === 'zoom-in' ? 'Zoom in to see this layer' : 'Zoom out to see this layer'}
              // Styled to match the warning chip, so the two read as one kind
              // of thing and the color distinguishes them rather than deciding
              // which is legible. This is the one you are meant to click.
              sx={{
                maxWidth: '100%',
                color: 'text.primary',
                borderColor: 'info.main',
                '& .MuiChip-icon': { color: 'info.main' },
              }}
            />
          )}
        </Box>
      )}

      <Collapse in={expanded} unmountOnExit>
        <Box sx={{ px: 2, pb: 1.5 }}>
          <Typography variant="caption" color="text.secondary" id={`opacity-${layer.name}`}>
            Opacity
          </Typography>
          <Slider
            size="small"
            value={Math.round(layer.opacity * 100)}
            onChange={(_, v) =>
              dispatch(layerOpacityChanged({ name: layer.name, opacity: (v as number) / 100 }))
            }
            valueLabelDisplay="auto"
            valueLabelFormat={(v) => `${v}%`}
            aria-labelledby={`opacity-${layer.name}`}
            sx={{ mt: 0 }}
          />

          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5, mb: 0.5 }}>
            Legend
          </Typography>
          <Box
            component="img"
            src={legendUrl(layer.name)}
            alt={`Legend for ${layer.title}`}
            loading="lazy"
            sx={{ maxWidth: '100%', display: 'block', bgcolor: 'background.paper', borderRadius: 0.5 }}
            onError={(e) => {
              // Not every layer publishes a legend; hide rather than show a broken image.
              (e.currentTarget as HTMLImageElement).style.display = 'none';
            }}
          />

          {(layer.minScale !== undefined || layer.maxScale !== undefined) && (
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
              Drawn {scaleRangeLabel(layer)}
            </Typography>
          )}

          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ display: 'block', mt: 1, fontFamily: 'ui-monospace, monospace', fontSize: '0.6875rem' }}
          >
            {layer.name}
          </Typography>
        </Box>
      </Collapse>
    </Box>
  );
}

function storeIndexLabel(storeIndex: number, total: number): string {
  return `${total - storeIndex} of ${total}`;
}

/**
 * The scale window in words.
 *
 * Phrased as "closer than" and "further out than" rather than as a pair of
 * denominators, because the denominator is the wrong way round from how the
 * range feels: a *smaller* number is a *closer* view.
 */
function scaleRangeLabel({ minScale, maxScale }: ActiveLayer): string {
  const ratio = (d: number) => `1:${Math.round(d).toLocaleString()}`;
  if (minScale !== undefined && maxScale !== undefined) {
    return `between ${ratio(maxScale)} and ${ratio(minScale)}`;
  }
  if (maxScale !== undefined) return `closer than ${ratio(maxScale)}`;
  if (minScale !== undefined) return `further out than ${ratio(minScale)}`;
  return 'at every zoom';
}
